/**
 * NewBadge — Marca un feature como "Nuevo" hasta que el usuario lo abre por
 * primera vez. Persiste en localStorage por usuario+featureKey.
 *
 * Uso:
 *   <NewBadge featureKey="cobranzas-aging">
 *     <Link to="/admin/cobranzas">Cobranzas</Link>
 *   </NewBadge>
 *
 * El badge desaparece cuando el contenedor recibe un click.
 */
import { useState, useEffect, useCallback, Children, cloneElement, isValidElement } from "react";
import { useAuth } from "../context/AuthContext.jsx";

function storageKey(uid, key) {
  return `crewos.seen.${uid || "anon"}.${key}`;
}

const MAX_AGE_DAYS = 14;

function parseReleaseDate(featureKey) {
  const m = /@(\d{4}-\d{2}-\d{2})$/.exec(featureKey || "");
  if (!m) return null;
  const d = new Date(m[1] + "T00:00:00Z");
  return Number.isFinite(d.getTime()) ? d : null;
}

function isExpired(featureKey) {
  const released = parseReleaseDate(featureKey);
  if (!released) return false;
  const ageDays = (Date.now() - released.getTime()) / 86_400_000;
  return ageDays > MAX_AGE_DAYS;
}

export function useNewFeature(featureKey) {
  const { user } = useAuth() || {};
  const uid = user?.uid || user?.id || "anon";
  const expired = isExpired(featureKey);
  const [vista, setVista] = useState(() => {
    if (expired) return true;
    try { return localStorage.getItem(storageKey(uid, featureKey)) === "1"; }
    catch { return true; }
  });

  const marcarVista = useCallback(() => {
    try { localStorage.setItem(storageKey(uid, featureKey), "1"); } catch {}
    setVista(true);
  }, [uid, featureKey]);

  return { esNueva: !expired && !vista, marcarVista };
}

export default function NewBadge({ featureKey, children, label = "NUEVO", className = "" }) {
  const { esNueva, marcarVista } = useNewFeature(featureKey);
  const handleClick = () => { marcarVista(); };

  // Si el hijo es un único elemento, le wraps un onClick que NO interrumpe el suyo.
  if (Children.count(children) === 1 && isValidElement(children)) {
    const original = children.props.onClick;
    const wrapped = (e) => { marcarVista(); if (original) original(e); };
    return (
      <span className={`relative inline-flex items-center ${className}`}>
        {cloneElement(children, { onClick: wrapped })}
        {esNueva && (
          <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold uppercase tracking-wider px-1 py-px rounded-sm bg-[#10B981] text-[#064E3B] leading-none">
            {label}
          </span>
        )}
      </span>
    );
  }
  // Caso fallback: wrap en un span con click
  return (
    <span onClick={handleClick} className={`relative inline-flex items-center ${className}`}>
      {children}
      {esNueva && (
        <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold uppercase tracking-wider px-1 py-px rounded-sm bg-[#10B981] text-[#064E3B] leading-none">
          {label}
        </span>
      )}
    </span>
  );
}
